import React from 'react';
import { getPatientTimezone } from 'contexts/PatientContext';
import { curveLinear } from 'd3-shape';
import { QuestionnaireInfo } from 'graphql/codegen/schemas';
import moment from 'moment-timezone';
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis, ReferenceArea } from 'recharts';
import { CHARTS_SIZES, GRAPH_REVERSE_TOOLTIP_COORDINATE } from '../../../../../utils/constants';
import { Tooltip as InfoTooltip } from '../../../tooltip';
import styles from '../../chart.module.scss';
import { getTicksDD, getTicksH } from '../../utlity';
import { PhqTotalToolTip } from './phq-total-tooltip';

type Props = {
  title: string;
  start: string;
  end: string;
  data: { ts: number; value: number }[];
  survey?: QuestionnaireInfo;
  showXAxis?: boolean;
};
const PhqTotalChart = ({ title, start, end, data, survey, showXAxis }: Props) => {
  const GRAPH_WIDTH_PX = CHARTS_SIZES.CDP_WIDTH;
  const GRAPH_HEIGHT_PX = showXAxis ? CHARTS_SIZES.UR_HEIGHT : CHARTS_SIZES.CDP_HEIGHT;
  const patientTimezone = getPatientTimezone();

  const customToolTip = ({
    active,
    coordinate,
    payload,
    label,
  }: {
    active: boolean;
    coordinate: { x: number; y: number };
    label: number;
    payload: Array<any>;
  }): JSX.Element | null => {
    if (!active) {
      return null;
    }
    const reverse = coordinate.x > GRAPH_REVERSE_TOOLTIP_COORDINATE;
    let yValue = payload ? payload[0]?.payload?.value : 'Unable to retrieve value';
    const title = yValue < 5 ? 'Minimal Depression' : yValue < 10 ? 'Mild Depression' : yValue < 15 ? 'Moderate Depression' : yValue < 20 ? 'Moderately Severe Depresssion' : 'Severe Depression'


    return <PhqTotalToolTip date={label} title={title} value={yValue} reverse={reverse} />;
  };

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartHeader}>
        <span className={styles.chartTitle}>Patient Health Questionnaire (PHQ-9)</span>
        <InfoTooltip portal baseStyles={`${styles.infoTooltip} ${styles.events}`} type='left' background='#F5F6FA'>
          <div
            dangerouslySetInnerHTML={{
              __html:
                'The Patient Health Questionnaire (PHQ) assesses 8 diagnoses, divided into threshold disorders (disorders that correspond to specific DSM-IV diagnoses: major depressive disorder, panic disorder, other anxiety disorder, and bulimia nervosa), and subthreshold disorders (disorders whose criteria encompass fewer symptoms than are required for any specific DSM-IV diagnoses: other depressive disorder, probable alcohol abuse/dependence, somatoform, and binge eating disorder).<br />\nThe PHQ-9 is the 9-item depression module from the full PHQ. Major depression is diagnosed if 5 or more of the 9 depressive symptom criteria have been present at least &quot;more than half the days&quot; in the past 2 weeks, and 1 of the symptoms is depressed mood or anhedonia. Other depression is diagnosed if 2, 3, or 4 depressive symptoms have been present at least &quot;more than half the days&quot; in the past 2 weeks, and 1 of the symptoms is depressed mood or anhedonia. One of the 9 symptom criteria (&quot;thoughts that you would be better off dead or of hurting yourself in some way&quot;) counts if present at all, regardless of duration. As with the original PRIME-MD, before making a final diagnosis, the clinician is expected to rule out physical causes of depression, normal bereavement, and history of a manic episode.<br />\nAs a severity measure, the PHQ-9 score can range from 0 to 27, since each of the 9 items can be scored from 0 (not at all) to 3 (nearly every day). An item was also added to the end of the diagnostic portion of the PHQ-9 asking patients who checked off any problems on the questionnaire: &quot;How difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?&quot;\n<ul style="margin-top: 10px;">\n<li style="background-color: rgba(209, 66, 86, 0.5); padding: 5px;width: 250px;">Score 20-27: Severe Depression</li>\n<li style="background-color: rgba(209, 66, 86, 0.3); padding: 5px;width: 250px;">Score 15-19: Moderately Severe Depresssion</li>\n<li style="background-color: rgba(253, 243, 184, 0.4); padding: 5px;width: 250px;">Score 10-14: Moderate Depression</li>\n<li style="background-color: rgba(65, 126, 185, 0.5); padding: 5px;width: 250px;">Score 5-9: Mild Depression</li>\n<li style="background-color: rgba(65, 126, 185, 0.3); padding: 5px;width: 250px;">Score 1-4: Minimal Depression</li></ul>\n',
            }}
          />
        </InfoTooltip>
      </div>
      <AreaChart width={GRAPH_WIDTH_PX} height={GRAPH_HEIGHT_PX} data={data}>
        <defs>
          <linearGradient id='d1' x1={0} y1={0} x2={0} y2={1}>
            <stop offset='3%' stopColor='#417EB9' stopOpacity={0.8} />
            <stop offset='97%' stopColor='rgba(65, 126, 185, 0.2)' stopOpacity={0} />
          </linearGradient>
        </defs>

        <XAxis
          xAxisId={0}
          dy={-10}
          dx={2}
          tickLine={true}
          tickSize={16}
          tickFormatter={(unixTime): string => moment.utc(unixTime).tz(patientTimezone).format('hA')}
          dataKey='ts'
          domain={[moment(start).unix() * 1000, moment(end).unix() * 1000]}
          allowDataOverflow={true}
          type='number'
          scale='time'
          textAnchor='start'
          ticks={getTicksH(start, end, GRAPH_WIDTH_PX)}
          mintickgap={0}
          interval={0}
          hide={!showXAxis}
        />
        <XAxis
          xAxisId={2}
          dy={-18}
          dx={0}
          style={{
            fontSize: '11',
            fontWeight: 'bold',
            fontFamily: 'sans-serif',
            color: '#000000',
          }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(unixTime): string => moment.utc(unixTime).format('ddd')}
          dataKey='ts'
          domain={[moment(start).unix() * 1000, moment(end).unix() * 1000]}
          allowDataOverflow={true}
          type='number'
          scale='time'
          textAnchor='start'
          ticks={getTicksDD(start, end, GRAPH_WIDTH_PX)}
          mintickgap={0}
          interval={0}
          hide={!showXAxis}
        />

        <XAxis
          xAxisId={3}
          dy={-34}
          dx={0}
          tickLine={false}
          axisLine={false}
          tickFormatter={(unixTime): string => moment.utc(unixTime).format('M/D')}
          dataKey='ts'
          domain={[moment(start).unix() * 1000, moment(end).unix() * 1000]}
          allowDataOverflow={true}
          type='number'
          scale='time'
          textAnchor='start'
          ticks={getTicksDD(start, end, GRAPH_WIDTH_PX)}
          mintickgap={0}
          interval={0}
          hide={!showXAxis}
        />
        <YAxis dataKey='value' interval={0} tickSize={0} domain={[0, 27]} ticks={[0, 5, 10, 15, 20, 27]} tickMargin={10} />
        <CartesianGrid stroke='#A3A6B3' />
        <Tooltip content={customToolTip} allowEscapeViewBox={{ x: false, y: true }} />
        <Area type={curveLinear} dataKey='value' stroke='#417EB9' fill='url(#d1)' strokeWidth='2' />
        <ReferenceArea
            x1={moment(start).unix() * 1000}
            x2={moment(end).unix() * 1000}
            y1={0}
            y2={5}
            fill='rgba(65, 126, 185, 0.3)'
          />,
          <ReferenceArea
            x1={moment(start).unix() * 1000}
            x2={moment(end).unix() * 1000}
            y1={5}
            y2={10}
            fill='rgba(65, 126, 185, 0.5)'
          />,
          <ReferenceArea
            x1={moment(start).unix() * 1000}
            x2={moment(end).unix() * 1000}
            y1={10}
            y2={15}
            fill='rgba(253, 243, 184, 0.4)'
          />,
          <ReferenceArea
            x1={moment(start).unix() * 1000}
            x2={moment(end).unix() * 1000}
            y1={15}
            y2={20}
            fill='rgba(209, 66, 86, 0.3)'
          />,
                    <ReferenceArea
            x1={moment(start).unix() * 1000}
            x2={moment(end).unix() * 1000}
            y1={20}
            y2={27}
            fill='rgba(209, 66, 86, .5)'
          />,
      </AreaChart>
    </div>
  );
};

export default PhqTotalChart;
